import React, { Component, Fragment } from 'react';
import { StickyContainer } from 'react-sticky';
import PropTypes from 'prop-types';

import { get } from 'core/libs/lodash';
import { compose } from 'core/libs/recompose';
import { withRouter } from 'core/libs/router';

import Logo from 'core/components/Logo';
import Auth from 'core/components/Auth';
import ShapkaMenu from 'core/components/ShapkaMenu';
import Promo from 'core/components/Promo';

import withMenu from 'core/hocs/withMenu';
import EmptyWrapper from 'core/components/EmptyWrapper';
import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';
import withUser from 'core/hocs/withUser';

import {
  DESKTOP_SMALL,
  MOBILE_BIG,
  DESKTOP_BIG,
  viewportBreakpoints,
} from 'core/components/breakpoint/viewport-values';

import themePropTypes from 'core/utils/prop-types/theme';
import { menuPropTypes } from 'core/utils/prop-types/relationships';
import { resolveScopedStyles } from 'core/utils/styled-jsx';
import { isRamblerIdEnabled } from 'core/utils/rcm';

import { BASE_Z_INDEX } from 'core/site/constants';

import StickyShapka from './StickyShapka';

import styles from './index.styl';


/**
 * Значение выбрано так, чтобы оно попадало в группу Main Content (REF 1)
 * и при этом не перекрывалось брендирование (REF 2)
 * REF: page 24 of https://www.iab.com/wp-content/uploads/2017/08/IABNewAdPortfolio_FINAL_2017.pdf
 * REF: https://jira.rambler-co.ru/browse/ADINBX-25415?focusedCommentId=3970127&page=com.atlassian.jira.plugin.system.issuetabpanels:comment-tabpanel#comment-3970127
 */
const Z_INDEX = {
  [true]: BASE_Z_INDEX,
  [false]: 4000,
};

export const sizeIndex = 0.72;

/**
 * @author Gordey Levchenko <g.levchenko@rambler-co.ru>
 */
class Shapka extends Component {
  static propTypes = {
    /**
     * Дополнительный элемент меню, который будет выводиться в конце.
     * На мобильный версии переносится внутрь меню.
     */
    additional: PropTypes.node,

    /**
     * Sticky-шапка в ширину на полный экран.
     */
    fullwidth: PropTypes.bool,

    /**
     * Дополнительный элемент, который отображается в правой части шапки.
     * В отличие от additional он не прячется в бургер на мобилке
     */
    extra: PropTypes.node,

    /** Главное меню */
    menu: menuPropTypes(),

    /** Дополнительные пункты меню (нужно для мобильного letidor)*/
    additionalMenuItems: PropTypes.array,

    /** @ignore */
    isDesktop: PropTypes.bool,
    /** @ignore */
    isMobile: PropTypes.bool,

    /** @ignore */
    theme: themePropTypes(`
      {
        controls: {
          shapka: {
            padding,
            shadow,
            background,
            backgroundOpened,
            height,
            dropdown: {
              fullwidth,
            },
          },
          personal: {
            headerButtonText: {
              textTransform,
              color,
              textDecoration,
              fontWeight
            }
          },
        },
      }
    `),

    /**
     * @ignore
     */
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      search: PropTypes.string.isRequired,
      key: PropTypes.string,
    }).isRequired,

    /**
     * Информация о заллогиненном пользователе, через систему авторизации rambler ID.
     */
    user: PropTypes.object,

    /**
     * Обработчик с помощью которого можно отслеживать состояние dropdown menu
     */
    handleChangeOpenState: PropTypes.func,

    /**
     * Справочник дополнительных элементов, подмешаных в меню
     *
     * В качестве ключа должен выступать индекс элемента меню, перед которым
     * должен встать дополнительный элемент
     * а в качестве значения компонент этого элемента
      */
    menuInjection: PropTypes.objectOf(PropTypes.func.isRequired),

    /**
     * Первая шапка для премиум топика. Отображается на фоне обложки.
     */
    isFirstShapkaForPremium: PropTypes.bool,
  };

  static contextTypes = {
    ramblerId: PropTypes.shape({
      rname: PropTypes.string,
    }),
    features: PropTypes.object,
  };

  state = {
    isOpen: false,
    isSmallerHeader: false,
    topPosition: true,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.location.key !== this.props.location.key) {
      this.setIsOpen(false);
    }
  }

  timeout = null;

  setIsOpen = isOpen => {
    this.setState({ isOpen: isOpen }, () => {
      if (this.props.handleChangeOpenState) {
        this.props.handleChangeOpenState(this.state.isOpen);
      }
    });
  };

  setScrollState = value => {
    this.setState({
      ...value,
    });
  };

  render() {
    const {
      additional,
      extra,
      theme: {
        controls: {
          shapka: shapkaStyles,
        },
        layout: {
          indents: {
            left: leftIndent,
            right: rightIndent,
          },
        },
      },
      isDesktop,
      isMobile,
      user,
      menu,
      additionalMenuItems,
      menuInjection,
      fullwidth,
      isFirstShapkaForPremium,
    } = this.props;

    const {
      features: {
        enableUgc,
      },
    } = this.context;

    let menuItems = get(menu, 'data.attributes.menu_items', []);
    if (additionalMenuItems) {
      menuItems = menuItems.concat(additionalMenuItems);
    }

    const {
      isOpen,
      isSmallerHeader,
      topPosition,
    } = this.state;

    const {
      sticky,
      [isOpen ? 'backgroundOpened' : 'background']: defaultBackground,
      stickyBackground,
      dropdown: {
        fullwidth: dropdownFullwidth,
      },
      padding,
      height,
      hideLogoInModal,
    } = shapkaStyles;

    const mobileOpened = isMobile && isOpen;

    const headerBackground = (!topPosition && sticky) ? stickyBackground : defaultBackground;
    const zIndex = Z_INDEX[isOpen];

    const showAuth = user.isAuthorized || (!mobileOpened && (enableUgc || isRamblerIdEnabled(this.context)));

    const scope = resolveScopedStyles(
      <scope>
        <style jsx>{`
          .${styles.header}
            padding ${padding}
            height ${height}px
            background ${(isFirstShapkaForPremium && !mobileOpened) ? 'none' : headerBackground}
            z-index ${zIndex}
            &._smallerHeader
              height ${height * sizeIndex}px
          .${styles.fullwidth}
            max-width ${viewportBreakpoints[isMobile ? MOBILE_BIG : DESKTOP_BIG]}
            min-width ${isMobile ? '0' : viewportBreakpoints[DESKTOP_SMALL]}
            justify-content ${mobileOpened && enableUgc && user.isAuthorized  ? 'space-between' : 'normal'}
            :global(.desktop) &
              padding 0 ${rightIndent}px 0 ${leftIndent}px
          .${styles.promo}
          .${styles.headerLeft}
            ._smallerHeader &
              transform scale(${sizeIndex})
        `}</style>
      </scope>
    );

    const FullwidthWrapper = fullwidth ? 'div' : EmptyWrapper;

    const header = (
      <Fragment>
        <header
          className={scope.wrapClassNames(
            styles.header,
            isSmallerHeader && '_smallerHeader'
          )}
        >
          <FullwidthWrapper className={scope.wrapClassNames(styles.fullwidth)}>
            {(!mobileOpened || !hideLogoInModal) && <Logo className={scope.wrapClassNames(styles.headerLeft)} />}
            {menuItems?.length > 0 && (
              <ShapkaMenu
                items={menuItems}
                additional={additional}
                isOpen={isOpen}
                setIsOpen={this.setIsOpen}
                menuInjection={menuInjection}
                isAuthorized={user.isAuthorized}
                isFirstShapkaForPremium={isFirstShapkaForPremium}
              />
            )}
            <div className={scope.wrapClassNames(styles.controls)}>
              {!isFirstShapkaForPremium && (
                <>
                  {isDesktop && additional}
                  {extra}
                  <Promo className={scope.wrapClassNames(styles.promo)} />
                </>
              )}
              {showAuth && <Auth asDropdown={enableUgc} /> }
            </div>
          </FullwidthWrapper>
        </header>
        {isDesktop && isOpen && dropdownFullwidth && <div className={styles.overlay} />}
        <scope.styles />
      </Fragment>
    );

    if (sticky) {
      return (
        <StickyShapka
          zIndex={zIndex}
          upperStateHandler={this.setScrollState}
          isSmallerHeader={isSmallerHeader}
          topPosition={topPosition}
          {...this.props}
        >
          {header}
        </StickyShapka>
      );
    }

    /**
     * TODO: Шапка полностью перерендеривается, когда меняется состояние
     * прилипания, хорошо бы это исправить.
     */
    return header;
  }
}

export { StickyContainer };

const ShapkaWithHOCs = compose(
  withMenu('main'),
  withTheme,
  withBreakpoint,
  withUser,
  withRouter,
)(Shapka);

ShapkaWithHOCs.displayName = 'Shapka';

export { Shapka as StorybookComponent };
export default ShapkaWithHOCs;
